import React, { useState, useEffect, useRef } from 'react';
import { apiFetch } from './api';

const SCREENS = [
    { id: 1, type: 'info', audio: 'screen_1', text: "Welcome to Maculy. This software will test your visual function. Over the next few minutes, we will guide you through how the test works." },
    { id: 2, type: 'info', audio: 'screen_2', text: "The test can be performed with your right eye, your left eye, or both eyes together. The eye not being tested must be covered. Your doctor will explain the exact procedure." },
    { id: 3, type: 'info', audio: 'screen_3', text: "In this test, we will determine your visual threshold for different targets, such as the striped circles shown here." },
    { id: 4, type: 'info', audio: 'screen_4', text: "Because we want to find the exact limit of your vision, you will inevitably be shown targets that are very difficult or impossible to see. This is completely normal." },
    { id: 5, type: 'interactive', audio: 'screen_5', cond: '3.0', intensity: -0.1, msg_hit: "screen_5_hit", msg_miss: "screen_5_miss" },
    { id: 6, type: 'interactive', audio: 'screen_6', cond: '3.0', intensity: -0.5, msg_hit: "screen_5_hit", msg_miss: "screen_6_miss" },
    { id: 7, type: 'interactive', audio: 'screen_7', cond: '3.0', intensity: -1.2, msg_hit: "screen_7_hit", msg_miss: "screen_7_miss" },
    { id: 8, type: 'interactive', audio: 'screen_8', cond: '3.0', intensity: -5.0, msg_hit: "screen_8_miss", msg_miss: "screen_8_miss", forcedHit: 0 },
    { id: 9, type: 'interactive', audio: 'screen_9', cond: '3.0', intensity: -5.0, msg_hit: "screen_9_miss", msg_miss: "screen_9_miss", forcedHit: -1 },
    { id: 10, type: 'interactive', audio: 'screen_10', cond: '3.0', intensity: -1.2, msg_hit: "screen_10_hit", msg_miss: "screen_10_miss" },
    { id: 11, type: 'info', audio: 'screen_11', text: "You are almost done. We will practice one more test: Color sensitivity. Here you see circles with different shades of blue." },
    { id: 12, type: 'interactive', audio: 'screen_12', cond: 'color_270', intensity: -0.1, msg_hit: "screen_12_hit", msg_miss: "screen_6_miss" },
    { id: 13, type: 'interactive', audio: null, cond: 'color_270', intensity: -0.5, msg_hit: null, msg_miss: "screen_6_miss" },
    { id: 14, type: 'interactive', audio: 'screen_14', cond: 'color_90', intensity: -0.5, msg_hit: null, msg_miss: "screen_6_miss" },
    { id: 15, type: 'info', audio: 'screen_15', text: "Great job. The tutorial is complete. Did you understand everything?" }
];

export default function TutorialScreen({ onComplete, onCancel, testModality }) {
    const [stepIndex, setStepIndex] = useState(0);
    const [images, setImages] = useState({});
    const [feedbackState, setFeedbackState] = useState(null); // 'fade-in' | 'fade-out' | null
    const [isCorrectFeedback, setIsCorrectFeedback] = useState(false);
    const [targetIndex, setTargetIndex] = useState(-1);
    const [feedbackTargetIndex, setFeedbackTargetIndex] = useState(-1);
    const [isAudioPlaying, setIsAudioPlaying] = useState(false);
    
    const audioRef = useRef(new Audio());
    const step = SCREENS[stepIndex];
    
    // Skip color steps if testModality is 'csf'
    useEffect(() => {
        if (testModality === 'csf' && SCREENS[stepIndex].id >= 11 && SCREENS[stepIndex].id <= 14) {
            setStepIndex(SCREENS.findIndex(s => s.id === 15));
        }
    }, [stepIndex, testModality]);

    const playAudio = async (filename, onEnded = null) => {
        if (!filename) {
            setTimeout(() => {
                if (onEnded) onEnded();
            }, 1000); // 1 second delay for visual feedback when no audio
            return;
        }
        
        audioRef.current.onended = null;
        audioRef.current.pause();
        audioRef.current.src = `/audio/${filename}.mp3`;
        setIsAudioPlaying(true);
        
        try {
            await audioRef.current.play();
            audioRef.current.onended = () => {
                setIsAudioPlaying(false);
                if (onEnded) onEnded();
            };
            audioRef.current.onerror = () => {
                console.warn("Audio playback error occurred");
                setIsAudioPlaying(false);
                if (onEnded) onEnded();
            };
        } catch (e) {
            console.warn("Audio play failed or was interrupted:", e);
            setIsAudioPlaying(false);
            if (onEnded) onEnded();
        }
    };

    useEffect(() => {
        // Preload example images to prevent "no image" bug
        const preload = async () => {
            const keys = [
                { cond: '3.0', intensity: -0.1, is_target: true },
                { cond: '3.0', intensity: -0.5, is_target: true },
                { cond: '3.0', intensity: -1.2, is_target: true },
                { cond: '3.0', intensity: -5.0, is_target: true },
                { cond: 'color_270', intensity: -0.1, is_target: true },
                { cond: 'color_270', intensity: -0.5, is_target: true },
                { cond: 'color_270', intensity: -0.1, is_target: false },
                { cond: 'color_90', intensity: -0.5, is_target: true },
                { cond: 'color_90', intensity: -0.1, is_target: false }
            ];
            
            for (let k of keys) {
                const cacheKey = k.is_target ? `${k.cond}_${k.intensity}` : `${k.cond}_blank`;
                if (!images[cacheKey]) {
                    const params = new URLSearchParams({
                        cond_key: k.cond,
                        intensity_log10: k.intensity,
                        patch_css_size: 150,
                        device_pixel_ratio: window.devicePixelRatio || 1,
                        is_target: k.is_target
                    });
                    try {
                        const res = await apiFetch(`/api/csf/example?${params.toString()}`);
                        const data = await res.json();
                        setImages(prev => ({ ...prev, [cacheKey]: data.image }));
                    } catch (err) {
                        console.error("Failed to preload image:", cacheKey, err);
                    }
                }
            }
        };
        preload();

        const audio = audioRef.current;
        return () => {
            audio.pause();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        playAudio(step.audio);
        if (step.type === 'interactive') {
            setTargetIndex(Math.floor(Math.random() * 4));
        }
    }, [stepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleNext = () => {
        if (stepIndex < SCREENS.length - 1) {
            setStepIndex(stepIndex + 1);
        } else {
            onComplete();
        }
    };

    const handlePlateClick = async (index) => {
        if (feedbackState !== null) return;
        
        let isCorrect = (index === targetIndex);
        if (step.forcedHit !== undefined) {
            isCorrect = (index === step.forcedHit) || (step.forcedHit === 0);
        }

        setIsCorrectFeedback(isCorrect);
        if (step.forcedHit !== undefined) {
            if (isCorrect) {
                setFeedbackTargetIndex(index);
            } else {
                let missTarget = targetIndex;
                if (missTarget === index) {
                    missTarget = (index + 1) % 4;
                }
                setFeedbackTargetIndex(missTarget);
            }
        } else {
            setFeedbackTargetIndex(targetIndex);
        }
        setFeedbackState('fade-in');
        
        const audioMsg = isCorrect ? step.msg_hit : step.msg_miss;
        playAudio(audioMsg, async () => {
            if (isCorrect || (step.id === 7 && !isCorrect) || (step.id >= 8 && step.id <= 9)) {
                // Proceed to next step after success (or special cases)
                setFeedbackState('fade-out');
                await new Promise(r => setTimeout(r, 250));
                setFeedbackState(null);
                handleNext();
            } else {
                // Retry
                setFeedbackState('fade-out');
                await new Promise(r => setTimeout(r, 250));
                setFeedbackState(null);
            }
        });
    };

    const renderInfo = () => (
        <div style={{ textAlign: 'center', maxWidth: '600px', margin: '0 auto' }}>
            {step.id === 1 && (
                <div style={{ marginBottom: '40px' }}>
                    <h1 style={{ fontSize: '3rem', color: '#339af0' }}>Maculy</h1>
                </div>
            )}
            {step.id === 2 && (
                <div style={{ marginBottom: '40px', fontSize: '4rem' }}>
                    👁️✋
                </div>
            )}
            <p style={{ fontSize: '1.5rem', lineHeight: '1.5', opacity: 0.9 }}>{step.text}</p>
            
            {step.id === 3 && (
                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
                    {images['3.0_-0.1'] ? (
                        <img src={images['3.0_-0.1']} alt="Example Target" style={{ width: '150px', height: '150px', borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)' }} />
                    ) : (
                        <div style={{ width: '150px', height: '150px', borderRadius: '50%', backgroundColor: 'rgba(255,255,255,0.1)' }}></div>
                    )}
                </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', width: '100%', maxWidth: '350px', margin: '50px auto 0' }}>
                {step.id === 1 ? (
                    <>
                        <button className="btn-danger" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={onCancel}>Cancel</button>
                        <button className="btn-primary" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={handleNext}>Next</button>
                    </>
                ) : step.id === 15 ? (
                    <>
                        <button className="btn-secondary" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={() => setStepIndex(0)}>Restart</button>
                        <button className="btn-primary" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={onComplete}>Return To Main Menu</button>
                    </>
                ) : (
                    <>
                        <button className="btn-secondary" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={() => setStepIndex(stepIndex - 1)}>Back</button>
                        <button className="btn-primary" style={{ flex: 1, padding: '15px', margin: 0, boxSizing: 'border-box' }} onClick={handleNext}>Next</button>
                    </>
                )}
            </div>
        </div>
    );

    const renderInteractive = () => {
        const cacheKey = `${step.cond}_${step.intensity}`;
        const blankKey = `${step.cond}_blank`;
        const targetImg = images[cacheKey];
        const blankImg = images[blankKey];
        const isColor = step.cond.startsWith('color_');
        
        return (
            <div style={{ textAlign: 'center' }}>
                <div className="test-header">
                    <div className="instruction" style={{ opacity: isAudioPlaying ? 1 : 0.7 }}>
                        {isAudioPlaying ? 'Listen...' : 'Your turn! Tap the target.'}
                    </div>
                </div>
                <div className="grid-container-csf" style={{ maxWidth: '600px', backgroundColor: isColor ? '#131313' : 'var(--bg-color)', margin: '0 auto' }}>
                    {[0, 1, 2, 3].map(i => {
                        const isTarget = (i === targetIndex);
                        const showFeedback = (feedbackState !== null && i === feedbackTargetIndex);
                        
                        let feedbackClass = "";
                        if (showFeedback) {
                            feedbackClass = isCorrectFeedback ? "feedback-correct" : "feedback-incorrect";
                        }
                        
                        return (
                            <div key={i} className="csf-plate" onClick={() => handlePlateClick(i)} style={{ backgroundColor: isColor ? '#131313' : 'var(--bg-color)', width: '150px', height: '150px' }}>
                                {(isTarget && targetImg) ? (
                                    <img src={targetImg} alt="Target" style={{ width: '150px', height: '150px' }} />
                                ) : (blankImg && isColor) ? (
                                    <img src={blankImg} alt="Blank" style={{ width: '150px', height: '150px' }} />
                                ) : (
                                    <div style={{ width: '150px', height: '150px' }}></div>
                                )}
                                {showFeedback && (
                                    <div className={`feedback-overlay ${feedbackState} ${feedbackClass}`}></div>
                                )}
                            </div>
                        );
                    })}
                </div>
                <div style={{ marginTop: '30px' }}>
                    <button className="btn-secondary" style={{ padding: '15px 30px', margin: 0, boxSizing: 'border-box' }} onClick={() => setStepIndex(stepIndex - 1)}>Back</button>
                </div>
            </div>
        );
    };

    const isColorTest = step.cond && step.cond.startsWith('color_');

    return (
        <div className="test-screen" style={{ 
            backgroundColor: isColorTest ? '#131313' : '#666666', 
            color: '#fff',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            padding: '20px'
        }}>
            {step.type === 'info' ? renderInfo() : renderInteractive()}
            
            <div style={{ position: 'absolute', top: 20, right: 20 }}>
                <button className="btn-secondary" style={{ padding: '5px 15px', fontSize: '0.9rem' }} onClick={onComplete}>Skip Tutorial</button>
            </div>
        </div>
    );
}
